import React, { useState, useEffect } from 'react';
import { Search, User, Check, Circle, MoreVertical, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { getLoanTransactions, formatLoanTransaction, getAllChildClinics } from '../../api/loan-action-creator';
import { formatAmount } from '../../utils/format';
import TransactionDrawer from './TransactionDrawer';

import './loanTransactions.css';

const PAGE_SIZE = 10;

const POST_APPROVAL_STATUS_CONFIG = [
  { key: 'kyc', label: 'KYC' },
  { key: 'faceVerification', label: 'Face verification' },
  { key: 'agreementSigned', label: 'Agreement signed' },
  { key: 'emiAutoPay', label: 'EMI auto pay' },
  { key: 'verificationCall', label: 'Verification call' },
  { key: 'nameMatchVerification', label: 'Name match verification' },
  { key: 'treatmentBill', label: 'Treatment bill' },
];

const statusFilterOptions = [
  { value: '', label: 'All statuses' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECT', label: 'Rejected' },
  { value: 'DISBURSED', label: 'Disbursed' },
];

const dateFilterOptions = [
  { value: '7days', label: 'Last 7 days' },
  { value: '30days', label: 'Last 30 days' },
  { value: '1month', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
  { value: 'all', label: 'All time' },
];

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getStatusPill = (status) => {
  const value = (status || '').toLowerCase();
  if (!status) return { tone: 'progress', label: '—' };
  if (value.includes('disburs') || value.includes('utr')) return { tone: 'disbursed', label: status };
  if (value.includes('reject')) return { tone: 'rejected', label: status };
  if (value.includes('application complete')) return { tone: 'complete', label: status };
  if (
    value.includes('approved') ||
    value.includes('successful') ||
    value.includes('emi plan selected') ||
    value.includes('agreement signed') ||
    value.includes('auto-pay setup complete') ||
    value.includes('kyc complete')
  ) {
    return { tone: 'approved', label: status };
  }
  if (value.includes('invoice') || value.includes('required') || value.includes('upload')) {
    return { tone: 'warning', label: status };
  }
  if (value.includes('progress') || value.includes('initiated') || value.includes('pending')) {
    return { tone: 'progress', label: status };
  }
  return { tone: 'progress', label: status };
};

const getChecklistScore = (transaction) => {
  const keys = POST_APPROVAL_STATUS_CONFIG.map((item) => item.key);
  const done = keys.filter((key) => Boolean(transaction.postApprovalChecklist?.[key])).length;
  return { done, total: keys.length };
};

const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/** Application IDs look like YYMMDD + sequence (e.g. 2608150099). Distinguishes from 10-digit phones. */
const looksLikeApplicationId = (value) => {
  const s = String(value || '').trim();
  if (!/^\d{10}$/.test(s)) return false;
  const month = parseInt(s.slice(2, 4), 10);
  const day = parseInt(s.slice(4, 6), 10);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
};

/** Indian mobile numbers are 10 digits starting with 6–9. */
const looksLikeMobileNumber = (value) => {
  const s = String(value || '').trim().replace(/\s+/g, '');
  return /^[6-9]\d{9}$/.test(s);
};

const LoanTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('');
  const [selectedClinicDoctorId, setSelectedClinicDoctorId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFilterType, setDateFilterType] = useState('30days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [showClinicFilter, setShowClinicFilter] = useState(false);

  const doctorId = localStorage.getItem('doctorId');

  const getStatusForAPI = (status) => {
    if (status === 'APPROVED' || status === 'REJECT' || status === 'DISBURSED') return status;
    return '';
  };

  const getDateRange = (overrides = {}) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const filterType = overrides.dateFilterType ?? dateFilterType;
    const customStart = overrides.startDate ?? startDate;
    const customEnd = overrides.endDate ?? endDate;

    if (filterType === '7days') {
      const start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      return {
        startDate: formatDateToDDMMYYYY(start.toISOString().split('T')[0]),
        endDate: formatDateToDDMMYYYY(todayStr),
      };
    }
    if (filterType === '30days') {
      const start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      return {
        startDate: formatDateToDDMMYYYY(start.toISOString().split('T')[0]),
        endDate: formatDateToDDMMYYYY(todayStr),
      };
    }
    if (filterType === '1month') {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
      return {
        startDate: formatDateToDDMMYYYY(start.toISOString().split('T')[0]),
        endDate: formatDateToDDMMYYYY(todayStr),
      };
    }
    if (filterType === 'custom') {
      if (!customStart || !customEnd) {
        return { startDate: '', endDate: '', incomplete: true };
      }
      return {
        startDate: formatDateToDDMMYYYY(customStart),
        endDate: formatDateToDDMMYYYY(customEnd),
      };
    }
    return { startDate: '', endDate: '' };
  };

  const fetchData = (overrides = {}) => {
    const clinic = overrides.selectedClinic ?? selectedClinic;
    const clinicDoctorId = overrides.selectedClinicDoctorId ?? selectedClinicDoctorId;
    const status = overrides.selectedStatus ?? selectedStatus;
    const search = (overrides.searchTerm ?? searchTerm).trim().replace(/\s+/g, '');
    const applicationId = looksLikeApplicationId(search) ? search : '';
    const mobileNumber = !applicationId && looksLikeMobileNumber(search) ? search : '';

    // Exact ID / phone search ignores date range so older apps are still findable
    const dateRange = (applicationId || mobileNumber)
      ? { startDate: '', endDate: '' }
      : getDateRange(overrides);
    if (dateRange.incomplete) {
      return;
    }

    setLoading(true);
    setError(null);
    const params = {
      loanStatus: getStatusForAPI(status),
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      clinicName: clinic,
      applicationId,
      loanId: '',
      mobileNumber,
    };
    const targetDoctorId = clinicDoctorId || doctorId;

    getLoanTransactions(
      targetDoctorId,
      params,
      (data) => {
        setTransactions(data.map(formatLoanTransaction));
        setLoading(false);
      },
      (err) => {
        if (err.status === 404 || err.message?.includes('404')) {
          setError(`No loan transactions found for "${clinic}". Please try selecting a different clinic or adjust your filters.`);
        } else {
          setError(err.message || 'Failed to fetch loan transactions');
        }
        setTransactions([]);
        setLoading(false);
      }
    );
  };

  const fetchClinics = () => {
    getAllChildClinics(
      doctorId,
      (data) => {
        setClinics(data);
        setShowClinicFilter(true);
      },
      () => setShowClinicFilter(false)
    );
  };

  useEffect(() => {
    fetchData();
    fetchClinics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClinicChange = (e) => {
    const selectedClinicName = e.target.value;
    setSelectedClinic(selectedClinicName);
    if (selectedClinicName) {
      const selectedClinicData = clinics.find((clinic) => clinic.clinicName === selectedClinicName);
      setSelectedClinicDoctorId(selectedClinicData?.doctorId || '');
    } else {
      setSelectedClinicDoctorId('');
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedClinic('');
    setSelectedClinicDoctorId('');
    setSelectedStatus('');
    setDateFilterType('30days');
    setStartDate('');
    setEndDate('');
    setPage(1);
    fetchData({
      searchTerm: '',
      selectedClinic: '',
      selectedClinicDoctorId: '',
      selectedStatus: '',
      dateFilterType: '30days',
      startDate: '',
      endDate: '',
    });
  };

  const handleApplyFilters = () => {
    if (dateFilterType === 'custom' && (!startDate || !endDate)) {
      return;
    }
    setPage(1);
    fetchData();
  };

  const handleDateFilterChange = (e) => {
    const nextType = e.target.value;
    setDateFilterType(nextType);
    if (nextType !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    if (!searchTerm) return true;
    const trimmed = searchTerm.trim().replace(/\s+/g, '');
    // Application ID / mobile are API-only — don't filter the current page as the user types
    if (
      looksLikeApplicationId(trimmed) ||
      looksLikeMobileNumber(trimmed) ||
      /^\d+$/.test(trimmed)
    ) {
      return true;
    }
    const query = searchTerm.trim().toLowerCase();
    return transaction.name?.toLowerCase().includes(query);
  });

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedTransactions = filteredTransactions.slice(startIndex, startIndex + PAGE_SIZE);
  const showingFrom = filteredTransactions.length === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + PAGE_SIZE, filteredTransactions.length);

  const renderPageShell = (body) => (
    <>
      <div className="lt-page">
        <div className="lt-page-head">
          <div>
            <h1>Loan transactions</h1>
            <p>Track applications, approvals, and disbursements.</p>
          </div>
        </div>

        <section className="lt-filters">
          <div className="lt-filters-head">
            <h2>Filters</h2>
            <button type="button" onClick={handleReset}>Reset</button>
          </div>
          <div className={`lt-filters-row${showClinicFilter ? ' has-clinic' : ''}${dateFilterType === 'custom' ? ' has-custom' : ''}`}>
            <div className="lt-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search name, ID, or phone"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyFilters();
                  }
                }}
              />
            </div>
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              {statusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select value={dateFilterType} onChange={handleDateFilterChange}>
              {dateFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {showClinicFilter && (
              <select value={selectedClinic} onChange={handleClinicChange}>
                <option value="">All clinics</option>
                {clinics.map((clinic, index) => (
                  <option key={index} value={clinic.clinicName}>
                    {clinic.clinicName}
                  </option>
                ))}
              </select>
            )}
            {dateFilterType === 'custom' && (
              <>
                <label className="lt-date-field">
                  <span>Start date</span>
                  <div className="lt-date-control">
                    <input
                      type="date"
                      value={startDate}
                      max={endDate || undefined}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <Calendar size={16} />
                  </div>
                </label>
                <label className="lt-date-field">
                  <span>End date</span>
                  <div className="lt-date-control">
                    <input
                      type="date"
                      value={endDate}
                      min={startDate || undefined}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                    <Calendar size={16} />
                  </div>
                </label>
              </>
            )}
            <button type="button" className="lt-apply-btn" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </section>

        <section className="lt-card">{body}</section>
      </div>
      {selectedTransaction && (
        <TransactionDrawer
          transaction={selectedTransaction}
          statusLabel={getStatusPill(selectedTransaction.status).label}
          statusTone={getStatusPill(selectedTransaction.status).tone}
          checklistItems={POST_APPROVAL_STATUS_CONFIG}
          onClose={() => setSelectedTransaction(null)}
          onInvoiceUploaded={fetchData}
        />
      )}
    </>
  );

  if (loading) {
    return renderPageShell(<div className="lt-empty">Loading transactions…</div>);
  }

  if (error) {
    return renderPageShell(
      <div className="lt-empty">
        <h3>No results found</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (filteredTransactions.length === 0) {
    return renderPageShell(
      <div className="lt-empty">
        <User size={28} />
        <h3>No transactions found</h3>
        <p>Try adjusting your filters or search terms</p>
      </div>
    );
  }

  return renderPageShell(
    <>
      <div className="lt-table-wrap">
        <div className="lt-grid" role="table">
          <div className="lt-grid-row lt-grid-head" role="row">
            <span>Applicant</span>
            <span>Application ID</span>
            <span>Amount</span>
            <span>Clinic / Product</span>
            <span>Status</span>
            <span>Checklist</span>
            <span>Applied</span>
            <span aria-hidden="true" />
          </div>
          {pagedTransactions.map((transaction) => {
            const pill = getStatusPill(transaction.status);
            const checklist = getChecklistScore(transaction);
            return (
              <div
                key={transaction.id}
                className="lt-grid-row"
                role="row"
                onClick={() => setSelectedTransaction(transaction)}
              >
                <div className="lt-applicant">
                  <span className="lt-avatar">{getInitials(transaction.name)}</span>
                  <div>
                    <strong>{transaction.name || '—'}</strong>
                    <span>{transaction.clinicName || '—'}</span>
                  </div>
                </div>
                <div className="lt-mono">{transaction.applicationId || transaction.loanId || '—'}</div>
                <div className="lt-amount">{formatAmount(transaction.loanAmountRaw ?? transaction.amount)}</div>
                <div className="lt-clinic">
                  <strong>{transaction.clinicName || '—'}</strong>
                  <span>{transaction.treatment || '—'}</span>
                </div>
                <div>
                  <span className={`lt-pill ${pill.tone}`}>{pill.label}</span>
                </div>
                <div>
                  {String(transaction.status || '').toLowerCase().includes('reject') ? (
                    <span className="lt-check-na">—</span>
                  ) : (
                    <div className={`lt-check${checklist.done === checklist.total && checklist.total > 0 ? ' complete' : ''}`}>
                      {checklist.done === checklist.total && checklist.total > 0 ? (
                        <Check size={14} />
                      ) : (
                        <Circle size={14} />
                      )}
                      {checklist.done}/{checklist.total}
                    </div>
                  )}
                </div>
                <div className="lt-date">{transaction.appliedDate || transaction.appliedAt || '—'}</div>
                <div className="lt-actions">
                  <button
                    type="button"
                    className="lt-more"
                    aria-label="Open details"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTransaction(transaction);
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="lt-pagination">
        <span>
          Showing {showingFrom}-{showingTo} of {filteredTransactions.length}
        </span>
        <div className="lt-page-controls">
          <button
            type="button"
            aria-label="Previous page"
            disabled={currentPage <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            aria-label="Next page"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

export default LoanTransactions;
